import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, MessageCircleQuestion, Target } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ScioMark } from "@/components/ui/scio-mark";
import { PublicHeader } from "@/components/layout/public-header";
import { LegalLinks } from "@/components/legal/legal-links";
import { FigureGallery, HeroBackdrop, HeroEyebrow, PillarCard, StatRow } from "./landing/chrome";

/**
 * What scio.lt shows somebody who is not signed in. Until now that was the login
 * card and nothing else — a visitor could not find out what the thing was without
 * an account they had no way to get.
 *
 * Every number and claim here is checked against the content in this repo (see
 * the counts in STATS). Nothing is said about the five subjects that have theory
 * but no questions yet: the page is about maths, because maths is what a learner
 * can actually open today.
 *
 * The figures are real. They are the same `/svg/` generators the questions are
 * drawn with, rendered live by this page — which is the one claim on it that a
 * visitor can check by looking.
 */

const STATS = [
  { value: "55 969", label: "matematikos užduočių" },
  { value: "1–9", label: "klasės" },
  { value: "152", label: "įgūdžiai su teorija" },
  { value: "8 159", label: "brėžiniai" },
];

const PILLARS = [
  {
    icon: Target,
    title: "Taikosi prie vaiko, o ne prie klasės",
    body: "Scio seka kiekvieną temą atskirai — ne „matematika 72 %“, o ką tiksliai vaikas jau moka ir ko dar ne. Kita užduotis parenkama pagal tai, kas šiandien silpniausia, ir nauja tema neatsidaro, kol neparuošta ankstesnė.",
  },
  {
    icon: MessageCircleQuestion,
    title: "Pokalbis, kuris neduoda atsakymo",
    body: "Užstrigus vaikas paspaudžia „Noriu suprasti“. Pokalbis paaiškina temą ir kartu išsprendžia panašų uždavinį su kitais skaičiais — bet šito uždavinio atsakymo nepasako niekada. Atsakymą vaikas įrašo pats.",
  },
  {
    icon: BookOpen,
    title: "Prie kiekvienos užduoties — teorija",
    body: "Kiekviena tema paaiškinta ir turi išspręstą pavyzdį — būtent tos sąvokos, kurios vaikas klausiamas, o ne bendrą skyriaus santrauką. Tėvai mato, kurią dalį vaikas praktikavo ir kurios dar ne.",
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader action="both" />

      {/* `isolate` so the backdrop's negative z-index stays inside <main> — without
          its own stacking context it slides behind the page background and the
          whole atmosphere disappears. */}
      <main className="relative isolate mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <HeroBackdrop />

        <section className="py-12 sm:py-20">
          <HeroEyebrow />
          <h1 className="max-w-3xl text-balance text-3xl font-bold leading-tight tracking-tight sm:text-5xl">
            Matematika, kuri žino, ko tavo vaikui trūksta
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground sm:text-xl">
            Beveik 56 000 užduočių 1–9 klasei lietuvių kalba. Scio seka kiekvieną temą
            atskirai, parenka, ką spręsti toliau, ir paaiškina — neduodamas atsakymo.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg">
              <Link to="/auth/register">
                Sukurti paskyrą
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground">
              Su Google paskyra — tėvams, mokytojams ir patiems mokiniams.
            </p>
          </div>

          <StatRow stats={STATS} />
        </section>

        <section className="grid gap-4 sm:grid-cols-3">
          {PILLARS.map((pillar) => (
            <PillarCard
              key={pillar.title}
              icon={pillar.icon}
              title={pillar.title}
              body={pillar.body}
            />
          ))}
        </section>

        <section className="mt-16 sm:mt-24">
          <h2 className="text-balance text-2xl font-bold tracking-tight sm:text-3xl">
            Kiekvienas brėžinys nupieštas pagal patį klausimą
          </h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Ne paveikslėlių biblioteka: laikrodis, kampas ar diagrama piešiami iš to paties
            uždavinio skaičių, todėl brėžinys negali prieštarauti sakiniui po juo. Ir jame
            niekada nerašomas dydis, kurio klausiama — kitaip užduotis atsakytų pati sau.
          </p>
          {/* Rendered live by this page from the same endpoint the questions use —
              the one claim here a visitor can verify by looking at it. Deliberately
              not `loading="lazy"`: these are a few KB of SVG each and they sit in one
              grid, so lazy loading bought nothing and left the last tiles empty on
              first paint. */}
          <FigureGallery />
          <p className="mt-3 text-sm text-muted-foreground">
            85 brėžinių tipai, 8 159 brėžiniai užduotyse.
          </p>
        </section>

        <section className="mt-16 grid gap-4 sm:mt-24 sm:grid-cols-2">
          <div className="rounded-2xl border bg-card p-6">
            <h2 className="text-lg font-semibold tracking-tight">Tėvams — kas iš tikrųjų vyksta</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Ataskaita parodo ne tik „18 iš 20“, bet ir ką vaikas išmoko per mėnesį, kur
              užstrigo ir ką konkrečiai daryti rytoj. Kiekvienas teiginys paremtas tuo, ką
              vaikas sprendė — ne nuojauta.
            </p>
          </div>
          <div className="rounded-2xl border bg-card p-6">
            <h2 className="text-lg font-semibold tracking-tight">Vaikui — laiškas, ne pažymys</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Vaikas gauna savo laišką: kas pavyko, kiek nueita ir vienas dalykas, kurį verta
              pabandyti toliau. Užduotys gali būti perrašytos apie tai, kas jam įdomu — tie
              patys skaičiai, kita istorija.
            </p>
          </div>
        </section>

        <section className="mt-16 rounded-2xl border bg-card p-8 text-center sm:mt-24 sm:p-12">
          <ScioMark size={40} className="mx-auto text-primary" />
          <h2 className="mt-4 text-balance text-2xl font-bold tracking-tight sm:text-3xl">
            Pradėkite nuo vienos sesijos
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Penkios užduotys parenkamos pagal tai, ką vaikas jau moka. Po jų matysite, kurios
            temos tvirtos, o kurias verta pakartoti.
          </p>
          <Button asChild size="lg" className="mt-6">
            <Link to="/auth/register">
              Sukurti paskyrą
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </section>
      </main>

      <footer className="border-t">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-6 text-sm text-muted-foreground sm:px-6">
          <span className="flex items-center gap-2">
            <ScioMark size={18} className="text-primary" />
            Scio
          </span>
          <span>Matematika 1–9 klasei lietuvių kalba</span>
          <LegalLinks className="ml-auto" />
          <Link to="/auth/login" className="underline underline-offset-4">
            Prisijungti
          </Link>
        </div>
      </footer>
    </div>
  );
}
